import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'components/Button';
import { useUnsavedChangesGuard } from 'components/OrganizationScope/context';
import { Input } from 'components/Input';
import {
  findRichMessageTemplate,
  getDefaultRichMessageTemplateId,
  RICH_MESSAGE_IMAGE_MAX_BYTES,
} from 'constants/richMessageTemplates';
import { lineStrings } from 'constants/strings';
import { useRichMessageTemplates } from 'hooks/useRichMessageTemplates';
import {
  buildRichMessageImagemapPayload,
  clampCustomHeight,
  createDefaultAreaActions,
  isLegacyImagemapImageUrl,
  readImageDimensions,
  uploadImagemapImage,
} from 'utils/richMessage';
import { RichMenuImageField } from '../RichMenu/Create/RichMenuImageField';
import { ERichMenuImageFieldLayout } from 'types';
import {
  ERichMessageTemplateCategory,
  type TRichMessageAreaAction,
} from 'types/richMessage';
import { RichMessageAreaActions } from './RichMessageAreaActions';
import { RichMessageGridPreview } from './RichMessageGridPreview';
import { RichMessageTemplateModal } from './RichMessageTemplateModal';
import {
  validateRichMessageCreateForm,
  type TRichMessageAreaFieldErrors,
} from './validateRichMessageCreate';

const strings = lineStrings.management.richMessage;

export type RichMessageEditorValues = {
  name: string;
  altText: string;
  templateId: string;
  imageUrl: string;
  imagemapBaseUrl?: string;
  areas: TRichMessageAreaAction[];
  customHeight?: number;
};

type Props = {
  initial?: Partial<RichMessageEditorValues>;
  isSaving: boolean;
  onSave: (
    payload: ReturnType<typeof buildRichMessageImagemapPayload>,
    name: string,
  ) => void;
  onDelete?: () => void;
  canDelete?: boolean;
  /** 保存が成功するたびに増える。今の入力を保存済みとして扱う合図。 */
  savedRevision?: number;
  onUploadPendingChange?: (pending: boolean) => void;
};

export function RichMessageEditor({
  initial,
  isSaving,
  onSave,
  onDelete,
  canDelete,
  savedRevision = 0,
  onUploadPendingChange,
}: Props) {
  const [name, setName] = useState(initial?.name ?? '');
  const [altText, setAltText] = useState(initial?.altText ?? '');
  const { data: templatesData, isLoading: isLoadingTemplates } =
    useRichMessageTemplates();
  const templates = useMemo(
    () => templatesData?.templates ?? [],
    [templatesData?.templates],
  );

  const [templateId, setTemplateId] = useState(
    initial?.templateId ?? getDefaultRichMessageTemplateId(templates),
  );
  const [imageUrl, setImageUrl] = useState(initial?.imageUrl ?? '');
  const [imagemapBaseUrl, setImagemapBaseUrl] = useState(
    initial?.imagemapBaseUrl ?? '',
  );
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    initial?.imageUrl ?? null,
  );
  const [customHeight, setCustomHeight] = useState<number | undefined>(
    initial?.customHeight,
  );
  const [areas, setAreas] = useState<TRichMessageAreaAction[]>([]);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [areaFieldErrors, setAreaFieldErrors] = useState<
    Record<number, TRichMessageAreaFieldErrors>
  >({});
  const [nameError, setNameError] = useState<string>();
  const [altTextError, setAltTextError] = useState<string>();
  const [imageError, setImageError] = useState<string>();
  const [expandAreaSignal, setExpandAreaSignal] = useState<{
    key: number;
    index: number;
  } | null>(null);
  const [imageWidth, setImageWidth] = useState<number | undefined>();

  const selectedTemplate = useMemo(
    () => findRichMessageTemplate(templates, templateId),
    [templateId, templates],
  );
  const markSaved = useUnsavedChangesGuard(
    JSON.stringify({
      name,
      altText,
      templateId,
      imageUrl,
      imagemapBaseUrl,
      imageFile: imageFile && [imageFile.name, imageFile.size, imageFile.lastModified],
      customHeight,
      areas,
    }),
    isUploadingImage,
  );
  const templatesSeededRef = useRef(false);
  useEffect(() => {
    if (savedRevision > 0) markSaved();
  }, [savedRevision, markSaved]);

  useEffect(() => {
    if (!templates.length) return;
    const defaultId = getDefaultRichMessageTemplateId(templates);
    setTemplateId((current) =>
      findRichMessageTemplate(templates, current) ? current : defaultId,
    );
    setAreas((prev) => {
      if (prev.length > 0) return prev;
      const tpl =
        findRichMessageTemplate(templates, initial?.templateId ?? '') ??
        findRichMessageTemplate(templates, defaultId);
      return tpl ? (initial?.areas ?? createDefaultAreaActions(tpl)) : [];
    });
    // テンプレート到着時の初期化は利用者の変更ではないため、その結果を基準にする
    if (!templatesSeededRef.current) {
      templatesSeededRef.current = true;
      markSaved();
    }
  }, [templates, initial?.templateId, initial?.areas, markSaved]);

  useEffect(() => {
    if (!selectedTemplate) return;
    setAreas((prev) => {
      if (prev.length === selectedTemplate.areaKeys.length) return prev;
      return createDefaultAreaActions(selectedTemplate);
    });
    setAreaFieldErrors({});
  }, [selectedTemplate]);

  useEffect(() => {
    if (!imageFile) {
      setPreviewUrl(imageUrl || null);
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile, imageUrl]);

  const handleImageFileChange = useCallback(
    async (file: File | null) => {
      setImageError(undefined);
      if (!file) {
        setImageFile(null);
        return;
      }
      if (file.size > RICH_MESSAGE_IMAGE_MAX_BYTES) {
        setImageError(lineStrings.management.richMessage.errors.imageTooLarge);
        return;
      }
      try {
        const dimensions = await readImageDimensions(file);
        setImageWidth(dimensions.width);
        if (
          selectedTemplate?.category === ERichMessageTemplateCategory.Custom
        ) {
          setCustomHeight(clampCustomHeight(dimensions.height));
        }
      } catch {
        setImageWidth(undefined);
      }
      setImageFile(file);
    },
    [selectedTemplate?.category],
  );

  const handleSave = useCallback(async () => {
    if (!selectedTemplate) return;

    const validation = validateRichMessageCreateForm({
      name,
      altText,
      hasImage: !!(imageFile || imageUrl.trim()),
      imageFileSize: imageFile?.size,
      imageWidth: imageFile ? imageWidth : 1040,
      customHeightInvalid:
        selectedTemplate.category === ERichMessageTemplateCategory.Custom &&
        customHeight !== undefined &&
        (customHeight < 520 || customHeight > 2080),
      areas,
    });

    setAreaFieldErrors(validation.areaErrors);
    setNameError(validation.nameError);
    setAltTextError(validation.altTextError);
    setImageError(validation.imageError);
    if (!validation.ok) {
      if (validation.firstErrorAreaIndex !== undefined) {
        setExpandAreaSignal({
          key: Date.now(),
          index: validation.firstErrorAreaIndex,
        });
      }
      return;
    }

    let resolvedImageUrl = imageUrl.trim();
    let resolvedImagemapBaseUrl = imagemapBaseUrl.trim();

    if (imageFile) {
      setIsUploadingImage(true);
      onUploadPendingChange?.(true);
      try {
        const uploaded = await uploadImagemapImage(imageFile);
        resolvedImageUrl = uploaded.imageUrl;
        resolvedImagemapBaseUrl = uploaded.imagemapBaseUrl;
        setImageUrl(resolvedImageUrl);
        setImagemapBaseUrl(resolvedImagemapBaseUrl);
        setImageFile(null);
      } catch {
        setImageError(
          lineStrings.management.richMessage.errors.imageUploadFailed,
        );
        return;
      } finally {
        setIsUploadingImage(false);
        onUploadPendingChange?.(false);
      }
    } else if (
      !resolvedImagemapBaseUrl &&
      isLegacyImagemapImageUrl(resolvedImageUrl)
    ) {
      setImageError(strings.errors.imagemapImageRequired);
      return;
    }

    const payload = buildRichMessageImagemapPayload({
      name,
      altText,
      templateId,
      templates,
      imageUrl: resolvedImageUrl,
      imagemapBaseUrl: resolvedImagemapBaseUrl,
      areas,
      customHeight,
    });

    onSave(payload, name.trim());
  }, [
    altText,
    areas,
    customHeight,
    imageFile,
    imageUrl,
    imageWidth,
    imagemapBaseUrl,
    name,
    onSave,
    onUploadPendingChange,
    selectedTemplate,
    templateId,
    templates,
  ]);

  if (isLoadingTemplates) {
    return (
      <p className="py-8 text-center text-sm text-muted">
        {strings.templatesLoading}
      </p>
    );
  }

  if (!templates.length) {
    return (
      <p className="py-8 text-center text-sm text-red-600">
        {strings.templatesLoadFailed}
      </p>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,280px)_1fr]">
      <div className="flex flex-col items-center gap-3">
        <RichMessageGridPreview
          template={selectedTemplate}
          imageUrl={previewUrl}
          showOutlines
        />
        {selectedTemplate?.category === ERichMessageTemplateCategory.Custom &&
        customHeight ? (
          <p className="text-xs text-muted">
            {strings.customSizeHint} — {customHeight}px
          </p>
        ) : null}
      </div>

      <div className="grid gap-4">
        <label className="grid gap-2 text-sm font-semibold">
          {strings.nameLabel}
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="font-normal"
          />
          {nameError ? (
            <span className="text-xs font-normal text-red-600">
              {nameError}
            </span>
          ) : null}
        </label>

        <label className="grid gap-2 text-sm font-semibold">
          {strings.altTextLabel}
          <Input
            value={altText}
            onChange={(e) => setAltText(e.target.value)}
            className="font-normal"
          />
          {altTextError ? (
            <span className="text-xs font-normal text-red-600">
              {altTextError}
            </span>
          ) : null}
        </label>

        <div className="rounded-2xl border border-border/60 bg-white p-4">
          <div className="flex items-center justify-between gap-3 pb-4">
            <span className="text-sm font-medium text-ink">
              {strings.templateLabel}: {selectedTemplate?.label ?? '—'}
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setTemplateModalOpen(true)}
            >
              {strings.templateChange}
            </Button>
          </div>
          <RichMenuImageField
            layout={ERichMenuImageFieldLayout.Stacked}
            onFileChange={handleImageFileChange}
            error={imageError}
          />
        </div>

        {selectedTemplate ? (
          <RichMessageAreaActions
            areaKeys={selectedTemplate.areaKeys}
            areas={areas}
            fieldErrors={areaFieldErrors}
            expandAreaSignal={expandAreaSignal}
            onChange={(index, next) => {
              setAreaFieldErrors((prev) => {
                const copy = { ...prev };
                delete copy[index];
                return copy;
              });
              setAreas((prev) => {
                const copy = [...prev];
                copy[index] = next;
                return copy;
              });
            }}
          />
        ) : null}

        <div className="flex justify-between">
          <Button
            type="button"
            variant="outline"
            disabled={!canDelete}
            onClick={onDelete}
          >
            {strings.delete}
          </Button>
          <Button
            type="button"
            loading={isSaving || isUploadingImage}
            disabled={!selectedTemplate}
            onClick={() => void handleSave()}
          >
            {strings.save}
          </Button>
        </div>
      </div>

      <RichMessageTemplateModal
        open={templateModalOpen}
        selectedId={templateId}
        templates={templates}
        onClose={() => setTemplateModalOpen(false)}
        onApply={(id) => {
          setTemplateId(id);
          setTemplateModalOpen(false);
        }}
      />
    </div>
  );
}
